import React, { FC, useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggingStyle,
  NotDraggingStyle,
  DropResult,
} from 'react-beautiful-dnd';
import { includes } from 'ramda';
import clone from 'lodash/clone';
import isNil from 'lodash/isNil';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CircularProgress from '@material-ui/core/CircularProgress';

import { getDimensions } from 'components/fileuploader/ImageUploader2';
import { KeycloakContext } from 'components/Secured';
import TagLabel from 'components/TagLabel';

import useSearchParams from 'hooks/useSearchParams';

import IProduct from 'models/product';

import { isDefined } from 'utils/helpers-ts';
import { getBlobDataFromImageURL } from 'utils/images2';
import { extractURLParam, updateUrlParams } from 'utils/urlUtils';
import { getInfo, resizeImage } from 'utils/images-ts';
import { addUpscaleImgix, isImgix, isUpscaledImgix, removeUpscaleImgix } from 'utils/imgix-ts';
import { isCloudinary } from 'utils/cloudinary-ts';

import ImgixBuilder from './ImgixBuilder';
import EditImageDialog from './EditImageDialog';
import AIImproveImpageButton from './AIImproveImpageButton';
import { AutogeneratedStyle, HiddenAutogeneratedStyle, MainImageStyle, StyledButton } from './ImageSorter.style';
import ViewImageDialog from './ViewImageDialog';

export interface IImage {
  pictureUrl?: string;
  isExisting?: boolean;
  imgSrc?: string;
  id: string;
  width?: number;
  height?: number;
  size?: number;
  sizeMB?: number;
  // ppi?: number;
  isApoteaImage?: boolean;
  isUpscaled?: boolean;
  needsUpscale?: boolean; // Auto-detected: both dimensions < 2401px
  upscaledWidth?: number; // Actual dimensions after upscaling (fetched from imgix)
  upscaledHeight?: number;
  fileName?: string;
}

const reorder = (list: IImage[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const isAutoGenerated = (image: IImage) => {
  const { pictureUrl } = image;
  return pictureUrl ? pictureUrl.includes('AIGENERATED') : false;
};

const isApoteaImage = (image: IImage) => {
  return image.isApoteaImage || false;
};

// const isOldImage = (image: IImage) => {
//   const { pictureUrl } = image;
//   if (pictureUrl?.includes('https://foodlab2b.imgix.net/')) return true;
//   return false;
// };

// const addUpscaleToImgixUrl = (url?: string): string | undefined => {
//   if (!isImgix(url)) return url;

//   try {
//     // Add or update the upscale parameter
//     return updateUrlParams(url, { upscale: 'true' });
//   } catch {
//     return url;
//   }
// };

// Get imgix info while preserving query parameters like upscale
const getInfoWithParams = async (url?: string) => {
  if (!url || url.trim() === '') return undefined;

  try {
    // Add fm=json to existing params instead of replacing all params
    const infoUrl = updateUrlParams(url, { fm: 'json' });
    const response = await fetch(infoUrl);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to fetch imgix info:', error);
  }
  return undefined;
};

const getUpscaleInfo = async (image: IImage) => {
  // If image needs upscale and has imgix URL, fetch actual upscaled dimensions
  let upscaledWidth: number | undefined = undefined;
  let upscaledHeight: number | undefined = undefined;
  let upscaledUrl = image.pictureUrl;
  const isLocalImage = !!image.imgSrc?.startsWith('blob:');

  try {
    upscaledUrl = addUpscaleImgix(image.pictureUrl);
    // Do not try to load a URL before the image has been uploaded,
    // otherwise this URL will return 404 permanently.
    if (!isLocalImage) {
      const upscaleInfo = await getInfoWithParams(upscaledUrl);
      if (upscaleInfo?.PixelWidth && upscaleInfo?.PixelHeight) {
        upscaledWidth = upscaleInfo.PixelWidth;
        upscaledHeight = upscaleInfo.PixelHeight;
      }
    }
  } catch (error: any) {
    console.error('Failed to fetch upscaled dimensions:', error.message);
  }

  return { upscaledUrl, upscaledWidth, upscaledHeight };
};

const getImageStyle = (isTobeRemoved: boolean, isCloudinaryUrl: boolean): React.CSSProperties => ({
  display: 'block',
  borderRadius: 2,
  maxWidth: '100%',
  height: 120,
  objectFit: 'contain',
  boxSizing: 'border-box',
  cursor: !isCloudinaryUrl ? 'pointer' : undefined,
  filter: isTobeRemoved ? 'blur(6px)' : undefined,
  userSelect: 'none',
  WebkitUserDrag: 'none',
} as React.CSSProperties);

const grid = 4;

const getItemStyle = (
  isDragging: boolean,
  isDropAnimating: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
  index: number
): React.CSSProperties => {
  const style: React.CSSProperties = {
    position: 'relative',
    maxHeight: 500,
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 ${grid}px 0 0`,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    boxSizing: 'border-box',
    background: isDragging ? 'rgba(173, 213, 255, 0.98)' : 'white',
    boxShadow: isDragging ? '0 6px 16px rgba(0, 0, 0, 0.15)' : undefined,
    willChange: 'transform',
    ...draggableStyle,
  };

  if (isDropAnimating && draggableStyle) {
    style.transition = 'transform 0.1s cubic-bezier(0.2, 0, 0, 1)';
  }

  return style;
};

export const downloadImage = async (src?: string) => {
  if (src) {
    const originalImageUrl = src.split('?')[0];
    const blob = await getBlobDataFromImageURL(src);
    let fileName = originalImageUrl.substr(originalImageUrl.lastIndexOf('/') + 1);

    const aLink = document.createElement('a');
    aLink.download = fileName;
    aLink.href = URL.createObjectURL(blob);
    aLink.target = '_blank';
    aLink.className = 'J_notOpenInContainer';
    document.body.appendChild(aLink);
    aLink.click();
    URL.revokeObjectURL(aLink.href);
  }
};

const imageIsToBeRemoved = (image?: IImage, imagesToRemove?: IImage[]) => {
  if (!image || !imagesToRemove) return false;
  return includes(image, imagesToRemove);
};

export const getImageMetadata = async (image: IImage) => {
  if (image.width && image.height) {
    return image;
  }
  // pictureUrl
  // id
  // imgSrc
  // isExisting
  // isNew
  let width = 0;
  let height = 0;
  let size = 0;
  let sizeMB = 0;
  // let ppi = 0; DPI
  try {
    if (image.isExisting) {
      const info = await getInfo(image.pictureUrl);
      // console.log('!!!info', info);
      if (info?.width) {
        width = info.width;
      }
      if (info?.height) {
        height = info.height;
      }
      if (info?.filesize) {
        size = info.filesize;
      }
      // const dpi = getDpi(info); DPI
      // if (dpi) { DPI
      //     ppi = dpi; DPI
      // }
      // if (info?.DPIWidth) {
      //     ppi = info.DPIWidth;
      // }
    } else {
      const result = await getDimensions(image?.imgSrc);
      width = result?.width;
      height = result?.height;
      size = result?.size || 0;
      // if (image?.imgSrc) {
      //   const response = await fetch(image.imgSrc);
      //   const data = await response.blob();
      //   const type = response.headers.get('content-type') || 'image/png'; // TODO
      //   const file = new File([data], 'notused.png', {
      //     type,
      //   });
      //   // ppi = await getFileDpi(file); DPI
      // }
    }
  } catch (error: any) {
    console.error('Failed to get metadata: ', error.message);
  }
  if (size > 0) {
    sizeMB = Math.ceil((size / (1024 * 1024)) * 10) / 10;
  }

  // Check if image needs upscaling (both dimensions below 2401px)
  const needsUpscale = width > 0 && height > 0 && width < 2401 && height < 2401;
  // Check if image upscaled
  const isUpscaled = isUpscaledImgix(image.pictureUrl);

  // If image needs upscale and has imgix URL, fetch actual upscaled dimensions
  let upscaledWidth: number | undefined = undefined;
  let upscaledHeight: number | undefined = undefined;

  if (needsUpscale && isImgix(image.pictureUrl)) {
    const upscaleInfo = await getUpscaleInfo(image);
    upscaledWidth = upscaleInfo.upscaledWidth;
    upscaledHeight = upscaleInfo.upscaledHeight;
  }

  const res: IImage = {
    ...image,
    width,
    height,
    size,
    sizeMB,
    needsUpscale,
    isUpscaled,
    upscaledWidth,
    upscaledHeight,
  };
  if (isNil(image.isApoteaImage) && res.pictureUrl && res.pictureUrl.trim() !== '') {
    res.isApoteaImage = !!extractURLParam('APOTEA_IMAGE', res.pictureUrl);
  }
  // const res = {...image, width, height, ppi};  DPI
  // console.log('!!!res', res);
  return res;
};

const getListStyle = (isDraggingOver: boolean) => ({
  marginTop: 20,
  marginBottom: 20,
  background: isDraggingOver ? 'rgba(173, 213, 255, 0.5)' : 'lightgrey',
  borderRadius: 3,
  display: 'flex',
  padding: grid,
  overflow: 'auto',
});

interface ImageSizesProps {
  image: IImage;
}

export const ImageSizes: FC<ImageSizesProps> = ({ image }) => {
  return (
    <div
      style={{
        margin: 2,
      }}
    >
      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          margin: 0,
        }}
      >
        <p
          style={{
            fontWeight: '700',
            margin: 0,
            color: (image?.sizeMB || 0) > 25 ? 'red' : 'black',
          }}
        >
          {image.sizeMB || 0} mb
        </p>
        {image.width || '??'}x{image.height || '??'}
      </p>
    </div>
  );
};

interface IImageSorterProps {
  canImproveImage?: boolean;
  newUi?: boolean;
  state?: IProduct;
  images?: IImage[];
  imagesToBeRemoved?: IImage[];
  showMetadata?: boolean;
  isDownload?: boolean;
  isEdit?: boolean;
  onOrderChanged: (images: IImage[]) => void;
  onDeleteOrKeepImagePressed: (image: IImage) => void;
  onAddedFiles?: (files: File[]) => void;
  containerStyle?: React.CSSProperties;
  disableEdit?: boolean;
  isMagazine?: boolean;
  showSizeWarning?: boolean;
  showApotea?: boolean;
}

const ImageSorter = ({
  canImproveImage,
  newUi,
  state,
  images,
  imagesToBeRemoved,
  onDeleteOrKeepImagePressed,
  onOrderChanged,
  onAddedFiles,
  containerStyle,
  showMetadata,
  isDownload,
  isEdit,
  disableEdit,
  isMagazine,
  showSizeWarning,
  showApotea,
}: IImageSorterProps) => {
  const { searchParams } = useSearchParams();
  const keycloakCtx = useContext(KeycloakContext);
  const isSuperuser = keycloakCtx.keycloak.hasResourceRole('superuser');
  // console.log('!!!images', images);
  const [isLoadingImagesMetadata, setIsLoadingImagesMetadata] = useState(false);
  const [newImages, setNewImages] = React.useState<IImage[]>([]);
  const [open, setOpen] = React.useState(false);
  const [url, setUrl] = React.useState<string | undefined>(undefined);
  const [imgSrc, setImgSrc] = React.useState<string | undefined>(undefined);
  const [viewImgIndex, setViewImgIndex] = React.useState<number | undefined>(undefined);
  const keepRef = useRef({ improvingImageMap: {} as Record<string, boolean> });
  const isDraggingRef = useRef(false);
  const ignoreNextClickRef = useRef(false);

  React.useEffect(() => {
    const handleWindowMouseDown = () => {
      if (!isDraggingRef.current) {
        ignoreNextClickRef.current = false;
      }
    };
    window.addEventListener('mousedown', handleWindowMouseDown, true);
    return () => {
      window.removeEventListener('mousedown', handleWindowMouseDown, true);
    };
  }, []);

  const viewableUrls = React.useMemo(() => {
    return (newImages || []).map(item => {
      const isLocal = !!item.imgSrc?.startsWith('blob:');
      if (isLocal) {
        // Return local blob URL for instant preview, fallback to empty data URI to prevent unwanted document requests
        return item.imgSrc || 'data:,';
      }
      // Prioritize full-size remote URL (pictureUrl) over the thumbnail (imgSrc) to avoid upscaling blur in the lightbox
      return item.pictureUrl
        ? resizeImage(item.pictureUrl, 2000, 2000, 'auto', true, true)
        : 'data:,';
    });
  }, [newImages]);
  React.useEffect(() => {
    if (showMetadata) {
      if (images && images.length > 0) {
        const needsFetch = images.some(img => !img.width || !img.height);
        if (needsFetch) {
          setNewImages(images);
          setIsLoadingImagesMetadata(true);
          Promise.all(images.map(img => (img.width && img.height ? img : getImageMetadata(img))))
            .then(setNewImages)
            .finally(() => setIsLoadingImagesMetadata(false));
        } else {
          setNewImages(images);
        }
      } else {
        setNewImages([]);
      }
    } else {
      // We do not show metadata, so just set images directly
      // Case: Producer Pictures
      setNewImages(images || []);
    }
  }, [images, showMetadata]);
  // console.log('!!!new', newImages);

  const toggleUpscale = async (image: IImage) => {
    const imageIndex = newImages.findIndex(img => img.id === image.id);
    if (imageIndex < 0) return;

    const updatedImage: IImage = {
      ...image,
      isUpscaled: !image.isUpscaled,
      upscaledWidth: undefined,
      upscaledHeight: undefined,
    };

    if (image.pictureUrl) {
      // If enabling upscale, fetch actual dimensions from imgix
      if (updatedImage.isUpscaled) {
        const { upscaledUrl, upscaledWidth, upscaledHeight } = await getUpscaleInfo(image);
        updatedImage.upscaledWidth = upscaledWidth;
        updatedImage.upscaledHeight = upscaledHeight;
        updatedImage.pictureUrl = upscaledUrl;
      } else {
        updatedImage.pictureUrl = removeUpscaleImgix(image.pictureUrl);
      }
    }

    const updatedImages = [...newImages];
    updatedImages[imageIndex] = updatedImage;

    onOrderChanged(updatedImages);
  };

  const onDragStart = () => {
    isDraggingRef.current = true;
    ignoreNextClickRef.current = false;
    document.dispatchEvent(new CustomEvent('ImageSorter_dragStart'));
  };

  const onDragEnd = (result: DropResult) => {
    isDraggingRef.current = false;
    ignoreNextClickRef.current = true;
    setTimeout(() => {
      ignoreNextClickRef.current = false;
    }, 150);
    document.dispatchEvent(new CustomEvent('ImageSorter_dragEnd'));
    if (!result.destination) {
      return;
    }

    const items = reorder(newImages, result.source.index, result.destination.index);
    setNewImages(items);
    onOrderChanged(items);
  };

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [currentImage, setCurrentImage] = React.useState<IImage | undefined>(undefined);

  const onApoteaImageToggle = (img: IImage) => {
    const imageIndex = newImages.findIndex(image => img.id === image.id);

    if (imageIndex < 0 || !img.pictureUrl) return;

    const updatedCurrentImage = { ...img };

    if (img.isApoteaImage) {
      updatedCurrentImage.isApoteaImage = false;
    } else {
      updatedCurrentImage.isApoteaImage = true;
    }

    const updatesImages = [...newImages];
    updatesImages[imageIndex] = updatedCurrentImage;

    onOrderChanged(updatesImages);
  };

  const addEditImage = (value?: string) => {
    if (value && currentImage && currentImage !== undefined && currentImage !== null) {
      const values = newImages ? clone(newImages) : [];
      const imgix = ImgixBuilder.parse(value);
      const newImage = {
        ...currentImage,
        imgSrc: imgix.getThumb(),
        pictureUrl: value,
        id: value,
      };
      values.splice(currentIndex + 1, 0, newImage);
      onOrderChanged(values);
    }
  };

  const formControlStyles = {
    margin: 0,
    justifyContent: 'center',
  };

  return isDefined(newImages) && newImages.length > 0 ? (
    <>
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        {/* TODO style={containerStyle}> */}
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={newUi ? undefined : getListStyle(snapshot.isDraggingOver)}
              className={newUi ? 'list-items' : undefined}
              {...provided.droppableProps}
            >
              {newImages.map((item, index) => {
                const isLocalImage = !!item.imgSrc?.startsWith('blob:');
                const canUpscale = !isLocalImage && isSuperuser && isImgix(item.pictureUrl);

                const onEditImage = () => {
                  if (disableEdit) return;
                  if (isDraggingRef.current || ignoreNextClickRef.current || snapshot.isDraggingOver || snapshot.isUsingPlaceholder) {
                    ignoreNextClickRef.current = false;
                    return;
                  }

                  setCurrentIndex(index);
                  setCurrentImage(item);
                  setUrl(item.pictureUrl);
                  if (isLocalImage) {
                    setImgSrc(item.imgSrc);
                  } else {
                    setImgSrc(item.pictureUrl);
                  }
                  setOpen(true);
                };
                const isTobeRemoved = imageIsToBeRemoved(item, imagesToBeRemoved);

                let displaySrc: string | undefined = item.imgSrc;
                if (!isLocalImage && item.imgSrc) {
                  if (item.isUpscaled) {
                    displaySrc = addUpscaleImgix(item.imgSrc);
                  } else {
                    displaySrc = removeUpscaleImgix(item.imgSrc);
                  }
                }

                let upscaleLabel = `Ingen sida över 2400px (${item.width}x${item.height})`;
                if (canUpscale) {
                  if (item.isUpscaled) {
                    upscaleLabel = `Upskalad med AI${
                      item.width && item.height ? ` (${item.width}x${item.height})` : ''
                    }: Klicka för att ta bort`;
                  } else {
                    upscaleLabel = `Ingen sida över 2400px (${item.width}x${item.height}): Klicka för att skala upp`;
                  }
                }

                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => {
                      const child = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, snapshot.isDropAnimating, provided.draggableProps.style, index)}
                          onClick={event => event.stopPropagation()}
                        >
                          {isLoadingImagesMetadata && (
                            <div
                              style={{
                                position: 'absolute',
                                background: 'rgba(255, 255, 255, 0.9)',
                                zIndex: 10,
                                padding: 16,
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 120 + 16, // image's height + padding
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CircularProgress size={28} />
                            </div>
                          )}
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: isCloudinary(item.pictureUrl) ? 'pointer' : undefined,
                            }}
                            onClick={() => {
                              if (isDraggingRef.current || ignoreNextClickRef.current || snapshot.isDragging) {
                                ignoreNextClickRef.current = false;
                                return;
                              }
                              if (isCloudinary(item.pictureUrl)) {
                                setViewImgIndex(index);
                              }
                            }}
                          >
                            <img
                              src={displaySrc}
                              style={getImageStyle(isTobeRemoved, isCloudinary(item.pictureUrl))}
                              alt="Not available"
                              draggable={false}
                              onDragStart={e => e.preventDefault()}
                              onClick={!isCloudinary(item.pictureUrl) ? onEditImage : undefined}
                            />
                          </div>
                          {index === 0 && <MainImageStyle>Main image</MainImageStyle>}
                          {isAutoGenerated(item) && <AutogeneratedStyle>Auto generated</AutogeneratedStyle>}
                          {!isAutoGenerated(item) && showMetadata && (
                            <HiddenAutogeneratedStyle>Auto generated</HiddenAutogeneratedStyle>
                          )}
                          {showApotea && (
                            <>
                              {index === 0 ? (
                                <AutogeneratedStyle>Huvudbild – skickas alltid till Apotea</AutogeneratedStyle>
                              ) : (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      disabled={isLoadingImagesMetadata || isTobeRemoved}
                                      checked={isApoteaImage(item)}
                                      onChange={() => onApoteaImageToggle(item)}
                                      name="apotea_image"
                                      color="primary"
                                      size="small"
                                      style={{ paddingRight: '5px', paddingLeft: 0 }}
                                    />
                                  }
                                  labelPlacement="end"
                                  label={
                                    <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                      Skicka till Apotea
                                    </span>
                                  }
                                  style={formControlStyles}
                                />
                              )}
                            </>
                          )}
                          {showMetadata && <ImageSizes image={item} />}
                          {showSizeWarning &&
                            (item.isUpscaled ||
                              (item.width && item.height && item.width < 2400 && item.height < 2400)) && (
                              <TagLabel
                                disabled={isLoadingImagesMetadata}
                                type={item.isUpscaled ? 'info' : 'warn'}
                                icon={<InfoOutlinedIcon fontSize="small" />}
                                label={upscaleLabel}
                                style={{
                                  flexDirection: 'column',
                                  width: 170,
                                  margin: '5px 0',
                                  cursor: canUpscale ? 'pointer' : 'default',
                                  textAlign: 'center',
                                }}
                                onClick={canUpscale ? () => toggleUpscale(item) : undefined}
                              />
                            )}
                          {canImproveImage && (
                            <div>
                              <AIImproveImpageButton
                                disabled={isLoadingImagesMetadata}
                                initialLoading={
                                  keepRef.current.improvingImageMap[
                                    (isLocalImage ? item.fileName : item.pictureUrl) || ''
                                  ]
                                }
                                isSuperuser={isSuperuser}
                                model={searchParams.model}
                                state={state}
                                imageData={item}
                                onImproved={file => onAddedFiles?.([file])}
                                onToggleLoading={bol => {
                                  keepRef.current.improvingImageMap[
                                    (isLocalImage ? item.fileName : item.pictureUrl) || ''
                                  ] = bol;
                                }}
                              />
                            </div>
                          )}
                          {/* <Tooltip title={isOldImage(item) ? "You can't remove this image. We are working on fixing it" : "" }> */}
                          <div>
                            {isTobeRemoved && (
                              <TagLabel
                                type="error"
                                icon={<InfoOutlinedIcon fontSize="small" />}
                                label="Uppdatera/spara produkten för att ta bort bilden"
                                style={{ flexDirection: 'column', maxWidth: 150, margin: '5px 0' }}
                              />
                            )}
                            <Button
                              fullWidth
                              disabled={isMagazine || isLoadingImagesMetadata}
                              size="small"
                              style={{ color: !isLoadingImagesMetadata ? '#B71C1C' : '' }}
                              onClick={() => onDeleteOrKeepImagePressed(item)}
                            >
                              {!isTobeRemoved && <DeleteOutlineIcon fontSize="small" />}
                              <span style={{ marginLeft: 4, marginTop: 2 }}>{isTobeRemoved ? 'Ångra' : 'Ta bort'}</span>
                            </Button>
                          </div>
                          {/* </Tooltip> */}
                          {isDownload && (
                            // <Tooltip title={isOldImage(item) ? "You can't download this image. We are working on fixing it" : "" }>
                            <div>
                              <StyledButton
                                disabled={isLoadingImagesMetadata}
                                onClick={() => {
                                  downloadImage(item.pictureUrl);
                                }}
                              >
                                Download
                              </StyledButton>
                            </div>
                            // </Tooltip>
                          )}
                          {isEdit && isImgix(item.pictureUrl) && (
                            // <Tooltip title={isOldImage(item) ? "You can't edit this image. We are working on fixing it" : "" }>
                            <div>
                              <StyledButton
                                disabled={disableEdit || isMagazine || isLoadingImagesMetadata}
                                // disabled={disableEdit || isOldImage(item) || isMagazine}
                                onClick={onEditImage}
                              >
                                Edit
                              </StyledButton>
                            </div>
                            // </Tooltip>
                          )}
                        </div>
                      );

                      if (snapshot.isDragging) {
                        return ReactDOM.createPortal(child, document.body);
                      }
                      return child;
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <EditImageDialog
        open={open}
        url={url}
        src={imgSrc}
        images={images}
        onClose={() => setOpen(false)}
        onSave={img => {
          addEditImage(img);
          setOpen(false);
        }}
      />
      {viewImgIndex !== undefined && (
          <ViewImageDialog
            srcs={viewableUrls}
            initialIndex={viewImgIndex}
            onClose={() => setViewImgIndex(undefined)}
          />
        )}
    </>
  ) : (
    <></>
  );
};

export default ImageSorter;
